/**
 * Playbooks Page (Spec 060) - Fully wired to API
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useBrand } from '../lib/brand-context.js';
import { apiGet, apiFetch } from '../lib/api.js';
import { useToast } from '../hooks/useToast.js';
import { EmptyState } from '../components/EmptyState.js';
import { CardListSkeleton } from '../components/Skeleton.js';
import type { PlaybookListItem, PlaybookListResponse } from '../types/index.js';
import { Icon, Spinner } from '../components/ui/Icon.js';
import { Button } from '../components/ui/Button.js';
import { RowList, Row, RowSnippet, RowFacts, RowFact } from '../components/ui/Row.js';
import { Chip } from '../components/ui/Chip.js';
import type { ChipProps } from '../components/ui/Chip.js';
import { StageLoop } from '../components/how-it-works/diagrams/LoopDiagram.js';

// Spec 235 task 3 — the chip-tone mapping (evidence.md): emerald/green -> ok,
// amber/yellow/orange -> warn, rose/red -> fail. slate is not a tone (a
// bare Chip with no `tone` renders neutral) — archived/low are genuinely
// "nothing to report", not a status worth colouring.
const STATUS_TONE: Record<string, ChipProps['tone']> = {
  generating: 'warn',
  draft: 'warn',
  active: 'ok',
  completed: 'ok',
  archived: undefined,
  failed: 'fail',
};

const PRIORITY_TONE: Record<string, ChipProps['tone']> = {
  critical: 'fail',
  high: 'fail',
  medium: 'warn',
  low: undefined,
};

export function PlaybooksPage() {
  const { selectedBrand, user } = useBrand();
  const { addToast } = useToast();
  const [playbooks, setPlaybooks] = useState<PlaybookListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevGeneratingRef = useRef(false);

  const fetchPlaybooks = useCallback(async () => {
    if (!selectedBrand || !user) return;
    setLoading(true);
    setError(null);
    try {
      const result = await apiGet<PlaybookListResponse>(
        `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/playbooks?limit=50`
      );
      setPlaybooks(result.data);

      // Check if any are still generating — poll every 3s
      const hasGenerating = result.data.some((p) => p.status === 'generating');
      if (hasGenerating && !pollRef.current) {
        pollRef.current = setInterval(() => {
          fetchPlaybooks();
        }, 3000);
      } else if (!hasGenerating && pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        // Notify if we transitioned from generating -> done
        if (prevGeneratingRef.current) {
          const failed = result.data.find((p) => p.status === 'failed');
          if (failed) {
            addToast(failed.error_message ?? 'Playbook generation failed', 'error');
          } else {
            addToast('Playbook generated successfully', 'success');
          }
        }
      }
      prevGeneratingRef.current = hasGenerating;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load playbooks');
    } finally {
      setLoading(false);
    }
  }, [selectedBrand, user]);

  useEffect(() => {
    fetchPlaybooks();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchPlaybooks]);

  const handleGenerate = async () => {
    if (!selectedBrand || !user) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/playbooks`, {
        method: 'POST',
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => null) as { error?: string } | null;
        throw new Error(errBody?.error ?? `Generation failed (${res.status})`);
      }
      const result = await res.json() as { data: { id: string; status: string } };
      const newId = result.data.id;

      // Add optimistic placeholder to the list immediately
      setPlaybooks((prev) => [
        {
          id: newId,
          title: 'Generating playbook...',
          status: 'generating',
          priority: null,
          summary: null,
          error_message: null,
          generated_at: null,
          created_at: new Date().toISOString(),
          action_count: 0,
        },
        ...prev,
      ]);

      // Start polling this specific playbook
      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await apiGet<{ data: PlaybookListItem }>(
            `/api/v1/tenants/${selectedBrand.tenant_id}/brands/${selectedBrand.id}/playbooks/${newId}`,
          );
          const pb = pollRes.data;
          if (pb.status === 'draft' || pb.status === 'active' || pb.status === 'completed') {
            clearInterval(pollInterval);
            addToast(`Playbook ready: ${pb.title} — ${pb.action_count} action${pb.action_count !== 1 ? 's' : ''}`, 'success');
            fetchPlaybooks();
          } else if (pb.status === 'failed') {
            clearInterval(pollInterval);
            addToast(pb.error_message ?? 'Playbook generation failed', 'error');
            fetchPlaybooks();
          }
        } catch {
          // polling error — keep trying
        }
      }, 3000);

      // Safety: stop polling after 5 minutes
      setTimeout(() => clearInterval(pollInterval), 300000);
    } catch (err) {
      const res = err instanceof Error ? err.message : 'Failed to generate playbook';
      setError(res);
    } finally {
      setGenerating(false);
    }
  };

  if (!selectedBrand) {
    return (
      <div className="hub-page--table">
        <h1 className="hub-page__title">Playbooks</h1>
        <div>
          <EmptyState icon="bx-book-open" title="Select a brand" description="Choose a brand to manage playbooks." />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="playbooks-page" className="hub-page--table">
      <div className="hub-page__head">
        <div>
          <h1 className="hub-page__title">Playbooks</h1>
          <p className="mt-1 text-sm text-slate-500">Optimization playbooks for {selectedBrand.name}</p>
          {/* Spec 256: the loop as navigation — this page's stage inverted, the next one click away. */}
          <div className="mt-3">
            <StageLoop current="playbooks" />
          </div>
        </div>
        <Button variant="primary" onClick={handleGenerate} disabled={generating} data-testid="generate-playbook-btn">
          {generating ? <Spinner /> : <Icon name="bx-plus" />}
          Generate playbook
        </Button>
      </div>

      {/* Generating status */}
      {playbooks.some((p) => p.status === 'generating') && (
        <div className="mt-4 flex items-center gap-3 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3" data-testid="playbook-generating-status">
          <Spinner className="text-violet-600" />
          <span className="text-sm font-medium text-violet-700">Generating playbook...</span>
          <span className="text-xs text-violet-500">This may take a minute</span>
        </div>
      )}

      {error && (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <Icon name="bx-error-circle" />
          {error}
        </div>
      )}

      {loading && <div className="mt-6"><CardListSkeleton cards={5} /></div>}

      {!loading && playbooks.length === 0 && (
        <div className="mt-8">
          <EmptyState
            icon="bx-book-open"
            title="No playbooks yet"
            description="Generate a playbook to get actionable optimization recommendations."
          />
        </div>
      )}

      {!loading && playbooks.length > 0 && (
        <RowList className="mt-6" data-testid="playbook-list">
          {playbooks.map((pb) => (
            // A generating playbook has nowhere to go yet — Row's `to` is
            // omitted rather than pointed at `#` with a preventDefault
            // onClick (the old workaround). That is exactly the disclosure
            // shape spec 235 §3.3 added Row for: no anchor, no stretched
            // ::after, so there is nothing to accidentally navigate.
            <Row
              key={pb.id}
              to={pb.status === 'generating' ? undefined : `/playbooks/${pb.id}`}
              data-testid={`playbook-${pb.id}`}
              title={pb.title}
              top={<Chip tone={STATUS_TONE[pb.status]}>{pb.status}</Chip>}
            >
              {pb.summary ? <RowSnippet>{pb.summary}</RowSnippet> : null}
              <RowFacts>
                <RowFact>{pb.action_count} action{pb.action_count !== 1 ? 's' : ''}</RowFact>
                {pb.generated_at ? <RowFact>Generated {new Date(pb.generated_at).toLocaleDateString()}</RowFact> : null}
                {pb.priority ? (
                  <RowFact>
                    <Chip tone={PRIORITY_TONE[pb.priority.toLowerCase()]}>{pb.priority}</Chip>
                  </RowFact>
                ) : null}
              </RowFacts>
              {pb.error_message ? <p className="mt-1 text-xs text-red-600">{pb.error_message}</p> : null}
            </Row>
          ))}
        </RowList>
      )}
    </div>
  );
}
